import { createSelector } from "reselect";
import get from "lodash/get";
import moment from "moment";
import { eligibilitySettingsSelector } from "./eligibilitySettings";
import { PLAN_TYPE } from "utils/constants";
import { loggedInUserDetailsSelector } from "./userProfileDetails";
import {
  addonsSelector,
  planServiceSelector,
  selectSelectedAddons
} from "./planService";
import { guestOrderSelector, userOrdersSelector } from "./guestOrder";
import { referralCodeSelector } from "./referralCode";
import { urlParam } from "utils/helpers";
import isEmpty from "lodash/isEmpty";
import { eligiblePlansSelector } from "./eligiblePlans";

const planSwitchPlansFormDomain = state =>
  get(state, "form.PlanSwitchPlansForm.values", {});

const planSwitchPlanDetailsFormDomain = state =>
  get(state, "form.PlanSwitchPlansForm.values", {});

const planSwitchOrderSummaryFormDomain = state =>
  get(state, "form.PlanSwitchPlansForm.values", {});

const planSwitchRequestOrderDomain = state =>
  get(state, "api.planSwitchOrderDetails.data", {});

// ***************************************************************
// ******************** Plan Switch selectors ********************
// ***************************************************************
export const planSwitchPlansFormSelector = createSelector(
  planSwitchPlansFormDomain,
  planSwitchPlansForm => planSwitchPlansForm
);

export const planSwitchPlanDetailsFormSelector = createSelector(
  planSwitchPlanDetailsFormDomain,
  planSwitchPlanDetailsForm => planSwitchPlanDetailsForm
);

export const planSwitchOrderSummaryFormSelector = createSelector(
  planSwitchOrderSummaryFormDomain,
  planSwitchOrderSummaryForm => planSwitchOrderSummaryForm
);

export const planSwitchRequestOrderDetailsSelector = createSelector(
  planSwitchRequestOrderDomain,
  planSwitchRequestOrder => planSwitchRequestOrder
);

export const planSwitchRequestOrderSelector = createSelector(
  planSwitchRequestOrderDetailsSelector,
  userOrdersSelector,
  (planSwitchRequestOrder, userOrders) => {
    const planSwitchOrder = get(planSwitchRequestOrder, "order", {});
    const userOrder = userOrders.find(
      order => order.order_ref === urlParam("request_id")
    );
    if (!isEmpty(planSwitchOrder)) {
      return { ...planSwitchOrder, steps: get(userOrder, "steps", []) };
    }

    return userOrder;
  }
);

export const planSwitchEligibilityDetailsSelector = createSelector(
  eligibilitySettingsSelector,
  eligibility => {
    return eligibility?.plan_switch;
  }
);

export const planSwitchEligibilitySelector = createSelector(
  planSwitchEligibilityDetailsSelector,
  eligibility => {
    return !!eligibility?.eligible;
  }
);

export const planSwitchInEligibilityReasonSelector = createSelector(
  planSwitchEligibilityDetailsSelector,
  eligibility => {
    return !eligibility?.eligible && eligibility?.meta?.reason;
  }
);

export const planSwitchSelectedPlanTypeSelector = createSelector(
  planSwitchEligibilitySelector,
  eligibility => {
    return eligibility ? PLAN_TYPE.POSTPAID : null;
  }
);

export const planSwitchCancelEligibilitySelector = createSelector(
  planSwitchRequestOrderDetailsSelector,
  request => {
    return get(request, "cancel_plan_switch_eligibility.eligible", false);
  }
);

export const planSwitchRequestPramsSelector = createSelector(
  planServiceSelector,
  loggedInUserDetailsSelector,
  addonsSelector,
  selectSelectedAddons,
  guestOrderSelector,
  referralCodeSelector,
  (plan, user, addons, selectedAddons, order, referralCode) => {
    const params = {
      channel: "web",
      orderType: "Service Order",
      items: [
        {
          sku: "",
          name: "PLAN_SWITCH",
          type: "REQUEST",
          identifiedBy: "",
          properties: [
            {
              key: "ACCOUNT_NUMBER",
              value: user?.telco_info?.service_instance_no
            },
            {
              key: "NUMBER",
              value: user?.telco_info?.phone_no
            },
            {
              key: "SOURCE_PLAN_ID",
              value: order?.plan?.sku
            }
          ]
        },
        {
          sku: plan?.sku,
          name: plan?.title,
          type: "PLAN",
          identifiedBy: "",
          properties: []
        },
        ...Object.keys(selectedAddons)
          .filter(key => selectedAddons[key])
          .map(sku => addons.find(addon => addon.id === sku))
          .map(addon => ({
            sku: addon.id,
            name: addon.name,
            type: "ADDON",
            identifiedBy: "",
            properties: []
          }))
      ],
      userDetails: {
        firstName: user?.first_name,
        lastName: user?.last_name,
        contactNumber: user?.telco_info?.phone_no,
        email: user?.email,
        dateOfBirth: moment(user?.dob?.timestamp).format("DD-MM-YYYY")
      },
      referralCode: referralCode?.value || null,
      auth: {}
    };

    return params;
  }
);

export const planSwitchDeliveryDetailsItemsSelector = createSelector(
  planSwitchRequestOrderDetailsSelector,
  details => {
    const timestamp = get(
      details,
      "cancel_plan_switch_eligibility.meta.details.schedule_timestamp"
    );

    return {
      planSwitchSchedule:
        timestamp &&
        `(${moment(timestamp).format("dddd")}) ${moment(timestamp).format(
          "DD/MM/YY"
        )}, 2:00-4:00pm `,
      requestId: urlParam("request_id")
    };
  }
);

export const planSwitchEligiblePlansSelector = createSelector(
  eligiblePlansSelector,
  guestOrderSelector,
  (plans, currentOrder) => {
    const filteredPlans = plans.filter(
      plan => plan.sku !== currentOrder?.plan?.sku
    );
    return filteredPlans;
  }
);

// ***************************************************************
// ******************* Plan Switch makeSelect ********************
// ***************************************************************
export const makeSelectPlanSwitchPlansForm = () => planSwitchPlansFormSelector;
export const makeSelectPlanSwitchEligiblePlans = () =>
  planSwitchEligiblePlansSelector;
export const makeSelectPlanSwitchPlanDetailsForm = () =>
  planSwitchPlanDetailsFormSelector;
export const makeSelectPlanSwitchOrderSummaryForm = () =>
  planSwitchOrderSummaryFormSelector;
export const makeSelectPlanSwitchEligibilityDetails = () =>
  planSwitchEligibilityDetailsSelector;
export const makeSelectPlanSwitchEligibility = () =>
  planSwitchEligibilitySelector;
export const makeSelectPlanSwitchSelectedPlanType = () =>
  planSwitchSelectedPlanTypeSelector;
export const makeSelectPlanSwitchRequestPrams = () =>
  planSwitchRequestPramsSelector;
export const makeSelectPlanSwitchDeliveryDetailsItems = () =>
  planSwitchDeliveryDetailsItemsSelector;
export const makeSelectPlanSwitchInEligibilityReason = () =>
  planSwitchInEligibilityReasonSelector;
export const makeSelectPlanSwitchRequestOrder = () =>
  planSwitchRequestOrderSelector;
export const makeSelectPlanSwitchCancelEligibility = () =>
  planSwitchCancelEligibilitySelector;
